import { useEffect, useState, useCallback } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { P, pub } from "@/lib/publicApi";
import { RefreshCw, MapPin } from "lucide-react";

const PRI_COLOR = (c) => c.call_status === "Closed" ? "#4B5563" : c.priority === 1 ? "#DC2626" : c.priority === 2 ? "#D97706" : "#1E7B4F";
const fmtT = (s) => s ? new Date(s).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "—";

export default function CfsBoard() {
  const [data, setData] = useState(null);
  const [disc, setDisc] = useState("");
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    const r = await pub.get("/cfs", { params: { discipline: disc || undefined, status: status || undefined } });
    setData(r.data);
  }, [disc, status]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const calls = data?.calls || [];
  const mapped = calls.filter((c) => c.lat != null && c.lng != null);
  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Live Calls for Service</h2>
          <p className="text-sm mt-1 max-w-3xl" style={{ color: P.dim }}>
            Sanitized feed from the agency's dispatch system. Officer names are never shown; sensitive incidents
            display block-level locations and publish only after the call is cleared. Publication delay: {data?.publication_delay_minutes ?? "—"} minutes.
          </p>
        </div>
        <div data-testid="cfs-last-updated" className="text-xs font-mono px-3 py-1.5 rounded-sm flex items-center gap-2" style={{ background: "#fff", border: `1px solid ${P.line}`, color: P.dim }}>
          <RefreshCw size={12} /> Last Updated: {data ? new Date(data.last_updated).toLocaleTimeString() : "…"}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["", "All Agencies"], ["police", "Police"], ["fire", "Fire"], ["ems", "EMS"]].map(([v, l]) => (
          <button key={v} data-testid={`cfs-filter-${l.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setDisc(v)} className="px-3 py-1.5 text-xs font-bold rounded-sm border"
            style={disc === v ? { background: P.navy, color: "#fff", borderColor: P.navy } : { background: "#fff", color: P.ink, borderColor: P.line }}>{l}</button>))}
        <span className="w-px" style={{ background: P.line }} />
        {[["", "All Calls"], ["active", "Active"], ["closed", "Closed"]].map(([v, l]) => (
          <button key={v} data-testid={`cfs-status-${(l || "all").replace(/\s/g, "-").toLowerCase()}`} onClick={() => setStatus(v)} className="px-3 py-1.5 text-xs font-bold rounded-sm border"
            style={status === v ? { background: P.gold, color: "#fff", borderColor: P.gold } : { background: "#fff", color: P.ink, borderColor: P.line }}>{l}</button>))}
        <span className="ml-auto text-xs self-center" style={{ color: P.dim }}>
          <span className="mr-3">🟢 Routine</span><span className="mr-3">🟡 Urgent</span><span className="mr-3">🔴 Emergency</span><span>⚫ Closed</span>
        </span>
      </div>
      <div data-testid="cfs-map" className="rounded-sm overflow-hidden border" style={{ borderColor: P.line, height: 380 }}>
        <MapContainer center={[39.955, -75.16]} zoom={12} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          {mapped.map((c) => (
            <CircleMarker key={c.incident_number} center={[c.lat, c.lng]} radius={8}
              pathOptions={{ color: PRI_COLOR(c), fillColor: PRI_COLOR(c), fillOpacity: 0.7 }}>
              <Popup>
                <div style={{ fontSize: 12, minWidth: 200 }}>
                  <b>{c.call_type}</b> — {c.priority_label}<br />
                  #{c.incident_number}<br />
                  {c.location}{c.address_precision === "block" ? " (block-level)" : ""}<br />
                  {c.units.join(", ") || "No units shown"}<br />
                  Status: {c.status}{c.disposition ? ` — ${c.disposition}` : ""}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      <div data-testid="cfs-board" className="border rounded-sm overflow-x-auto" style={{ borderColor: P.line, background: "#fff" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: P.dim, background: "#F1EFE8" }}>
              {["Incident #", "Received", "Call Type", "Priority", "Location", "Units", "Dispatched", "Arrived", "Status", "Disposition"].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.incident_number} data-testid={`cfs-row-${c.incident_number}`} className="border-t" style={{ borderColor: P.line }}>
                <td className="px-3 py-2 font-mono text-xs font-bold" style={{ color: P.navy }}>{c.incident_number}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: P.ink }}>{new Date(c.received_at).toLocaleDateString()} {fmtT(c.received_at)}</td>
                <td className="px-3 py-2 text-xs font-semibold" style={{ color: P.ink }}>{c.call_type}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap"><span className="px-1.5 py-0.5 rounded-sm text-white text-[10px] font-bold" style={{ background: PRI_COLOR(c) }}>{c.priority_label}</span></td>
                <td className="px-3 py-2 text-xs" style={{ color: P.ink }}><MapPin size={11} className="inline mr-1" style={{ color: P.dim }} />{c.location}</td>
                <td className="px-3 py-2 text-xs font-mono" style={{ color: P.ink }}>{c.units.join(", ") || "—"}</td>
                <td className="px-3 py-2 text-xs" style={{ color: P.dim }}>{fmtT(c.dispatch_time)}</td>
                <td className="px-3 py-2 text-xs" style={{ color: P.dim }}>{fmtT(c.arrival_time)}</td>
                <td className="px-3 py-2 text-xs font-semibold" style={{ color: c.call_status === "Closed" ? P.dim : P.green }}>{c.status}</td>
                <td className="px-3 py-2 text-xs" style={{ color: P.ink }}>{c.disposition || "—"}</td>
              </tr>
            ))}
            {!calls.length && <tr><td colSpan={10} className="px-3 py-8 text-center text-sm" style={{ color: P.dim }}>No publishable calls at this time.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
